import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSHomeAddressData,
  type EnrollmentHYCSHomeAddressSubmitInput,
  type EnrollmentHYCSHomeAddressSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS HomeAddress data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSHomeAddressData>
 * @example const response = await getEnrollmentHYCSHomeAddressData()
 */
export const getEnrollmentHYCSHomeAddressData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSHomeAddressData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSHomeAddressData',
      query: `mutation getEnrollmentHYCSHomeAddressData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSHomeAddressData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSHomeAddressData = createQueryHook<undefined, EnrollmentHYCSHomeAddressData>(
  getEnrollmentHYCSHomeAddressData,
)

/**
 * Submit Enrollment HYCS HomeAddress data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSHomeAddressSubmitOutput>
 * @example const response = await submitEnrollmentHYCSHomeAddressData();
 */
export const submitEnrollmentHYCSHomeAddressData = async (
  enrollmentHomeAddressSubmitInput: EnrollmentHYCSHomeAddressSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSHomeAddressSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSHomeAddressData',
      query: `mutation submitEnrollmentHYCSHomeAddressData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHomeAddressSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSHomeAddressSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSHomeAddressData = createMutationHook<
  EnrollmentHYCSHomeAddressSubmitInput,
  EnrollmentHYCSHomeAddressSubmitOutput
>(submitEnrollmentHYCSHomeAddressData)
